<template>
  <div class="bg-light min-vh-100">
    <main class="container">
      <div class="row justify-content-center">
        <div class="col-lg-10">
          <div class="card shadow-sm">
            <div class="card-body">
              <!-- Header section with title and description -->
              <div
                class="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2"
              >
                <h1 class="h4 mb-0">Issues</h1>
                <p class="help mb-0 text-muted">
                  Listing all issues across devices you can access.
                </p>
              </div>

              <!-- Loading spinner -->
              <div v-if="loading" class="text-center py-5">
                <div class="spinner-border" role="status"></div>
              </div>

              <!-- Error message -->
              <div v-else-if="error" class="alert alert-danger">
                {{ error }}
              </div>

              <!-- Warning if orders failed to load (device/serial info incomplete) -->
              <div v-if="ordersError && !error && !loading" class="alert alert-warning py-2 mb-3">
                {{ ordersError }}
              </div>

              <!-- Empty state message -->
              <div v-else-if="groupedIssues.length === 0" class="text-muted">
                No issues found.
              </div>

              <!-- Main content: grouped issues by order -->
              <div v-else class="mt-2">

                <!-- Filter controls -->
                <div class="row g-2 mb-3">
                  <!-- Status dropdown filter -->
                  <div class="col-md-4">
                    <label class="form-label small mb-1 d-flex justify-content-between">
                      <span>Status</span>
                      <!-- Display count of filtered results -->
                      <span class="text-muted">
                        {{ filteredIssueCount }} issues matching filters
                      </span>
                    </label>
                    <select v-model="statusFilter" class="form-select form-select-sm">
                      <option value="">All statuses</option>
                      <option
                        v-for="opt in statusOptions"
                        :key="opt"
                        :value="opt"
                      >
                        {{ opt }}
                      </option>
                    </select>
                  </div>

                  <!-- Device type dropdown filter -->
                  <div class="col-md-4">
                    <label class="form-label small mb-1">Device</label>
                    <select v-model="deviceFilter" class="form-select form-select-sm">
                      <option value="">All devices</option>
                      <option
                        v-for="dev in deviceOptions"
                        :key="dev"
                        :value="dev"
                      >
                        {{ dev }}
                      </option>
                    </select>
                  </div>

                  <!-- Serial number search input -->
                  <div class="col-md-4">
                    <label class="form-label small mb-1">Serial search</label>
                    <input
                      v-model="serialSearch"
                      type="text"
                      class="form-control form-control-sm"
                      placeholder="Search by serial number..."
                    />
                  </div>
                </div>

                <!-- Message when filters return no results -->
                <div v-if="filteredIssueCount === 0" class="text-muted">
                  No issues match the current filters.
                </div>

                <div
                  v-for="group in filteredGroups"
                  :key="group.orderId"
                  class="mb-4"
                >
                  <!-- Order header with link to order details -->
                  <div
                    class="d-flex justify-content-between align-items-center mb-1"
                  >
                    <div class="fw-semibold">
                      <span v-if="orderLookup[group.orderId]">
                        {{ orderLookup[group.orderId].device_commonname }} |
                        {{ orderLookup[group.orderId].serial_number }}
                      </span>
                      <span v-else>
                        Device #{{ group.orderId }}
                      </span>
                    </div>
                    <RouterLink
                      class="btn btn-outline-primary btn-sm"
                      :to="`/orders/${group.orderId}`"
                    >
                      View Device
                    </RouterLink>
                  </div>

                  <!-- Table displaying issues for this order -->
                  <div class="table-responsive">
                    <table class="table table-sm align-middle mb-1">
                      <thead class="table-light">
                        <tr>
                          <th style="width: 140px;">Created</th>
                          <th style="width: 90px;">Severity</th>
                          <th style="width: 130px;">Status</th>
                          <th>Description</th>
                          <th>Resolution</th>
                          <th style="width: 160px;">Resolved By</th>
                        </tr>
                      </thead>
                      <tbody>
                        <!-- Issue row for each issue in the group -->
                        <tr
                          v-for="issue in group.issues"
                          :key="issue.issue_id"
                        >
                          <td class="small text-muted">
                            {{ issue.created_timestamp }}
                          </td>
                          <td class="small">
                            {{ issue.issue_severity }}
                          </td>
                          <td class="small">
                            {{ issue.issue_status }}
                          </td>
                          <td class="small">
                            {{ issue.issue_description }}
                          </td>
                          <!-- Show resolution summary or em-dash if empty -->
                          <td class="small">
                            <span
                              v-if="
                                issue.resolution_summary &&
                                issue.resolution_summary.trim()
                              "
                            >
                              {{ issue.resolution_summary }}
                            </span>
                            <span v-else>—</span>
                          </td>
                          <td class="small">
                            <span v-if="issue.resolved_by_username">
                              {{ issue.resolved_by_username }}
                            </span>
                            <span v-else>—</span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              <!-- end groups -->
            </div>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import { APIService } from "@/http/APIService";

export default {
  name: "IssuesList",
  data() {
    return {
      api: new APIService(),
      loading: false,   // tracks loading state
      error: null,      // stores error message if fetch fails
      ordersError: null, // stores error if orders fetch fails
      issues: [],       // stores raw issues from API
      orders: [],       // stores orders for lookup

      // Filter state
      statusFilter: "",    // filter by issue_status
      deviceFilter: "",    // filter by device_commonname
      serialSearch: "",    // search by serial number
    };
  },

  computed: {
    // Map of order_id -> order object (for device_type / serial_number)
    orderLookup() {
      const map = {};
      const list = Array.isArray(this.orders) ? this.orders : [];
      for (const o of list) {
        if (o && o.order_id != null) {
          map[o.order_id] = o;
        }
      }
      return map;
    },

    // Groups issues by order ID and sorts by most recent orderId first
    groupedIssues() {
      if (!Array.isArray(this.issues) || this.issues.length === 0) {
        return [];
      }

      const groups = {};

      // Build groups object keyed by orderId
      for (const issue of this.issues) {
        const orderId = issue.order; // backend returns order pk in "order"
        if (!groups[orderId]) {
          groups[orderId] = {
            orderId,
            issues: [],
          };
        }
        groups[orderId].issues.push(issue);
      }

      // Convert to array and sort by orderId descending
      return Object.values(groups).sort((a, b) => b.orderId - a.orderId);
    },

    // Extract unique issue statuses for status filter
    statusOptions() {
      const statuses = this.issues
        .map((i) => i.issue_status)
        .filter(Boolean);
      return [...new Set(statuses)].sort();
    },

    // Extract unique device names from orders for device filter
    deviceOptions() {
      const names = this.orders
        .map((o) => o.device_commonname)
        .filter(Boolean);
      return [...new Set(names)].sort();
    },

    // Apply filters to the grouped issues list
    filteredGroups() {
      const status = this.statusFilter;
      const device = this.deviceFilter;
      const query = this.serialSearch.trim().toLowerCase();

      const result = [];

      for (const baseGroup of this.groupedIssues) {
        const order = this.orderLookup[baseGroup.orderId] || null;
        const deviceName = order?.device_commonname || "";
        const serial = (order?.serial_number || "").toLowerCase();

        // Filter issues within this group
        const matchingIssues = baseGroup.issues.filter((issue) => {
          const matchesStatus = !status || issue.issue_status === status;
          const matchesDevice = !device || deviceName === device;
          const matchesSerial = !query || serial.includes(query);
          return matchesStatus && matchesDevice && matchesSerial;
        });

        if (matchingIssues.length > 0) {
          result.push({
            orderId: baseGroup.orderId,
            issues: matchingIssues,
          });
        }
      }

      return result;
    },

    // Total count of issues after filters applied
    filteredIssueCount() {
      return this.filteredGroups.reduce(
        (sum, group) => sum + group.issues.length,
        0
      );
    },
  },

  created() {
    // Fetch issues and orders when component is created
    this.fetchIssues();
    this.fetchOrders();
  },

  mounted() {
    // Restore filters from URL query params
    this.restoreFiltersFromUrl();
  },

  watch: {
    // Sync filter changes to URL
    statusFilter() {
      this.updateUrlQueryParams();
    },
    deviceFilter() {
      this.updateUrlQueryParams();
    },
    serialSearch() {
      this.updateUrlQueryParams();
    },
  },

  methods: {
    // Fetch all issues from API
    async fetchIssues() {
      this.loading = true;
      this.error = null;

      try {
        const accessToken = localStorage.getItem("accessToken");
        const response = await this.api.listAllIssues(accessToken);
        this.issues = Array.isArray(response.data) ? response.data : [];
      } catch (err) {
        console.error("Failed to load issues:", err);
        this.error = "Unable to load issues.";
      } finally {
        this.loading = false;
      }
    },

    // Fetch all orders for device/serial lookup
    async fetchOrders() {
      this.ordersError = null;
      try {
        const accessToken = localStorage.getItem("accessToken");
        const res = await this.api.getOrders(accessToken);
        this.orders = Array.isArray(res.data) ? res.data : [];
      } catch (err) {
        console.error("Failed to load orders for issues view:", err);
        this.ordersError = "Unable to load device details. Device names and serial numbers may not display.";
      }
    },

    // Persist filter state to URL query params
    updateUrlQueryParams() {
      const query = {};
      if (this.statusFilter) query.status = this.statusFilter;
      if (this.deviceFilter) query.device = this.deviceFilter;
      if (this.serialSearch.trim()) query.serial = this.serialSearch.trim();

      // Only update if query has changed
      const currentQuery = this.$route.query;
      const hasChanged =
        query.status !== currentQuery.status ||
        query.device !== currentQuery.device ||
        query.serial !== currentQuery.serial;

      if (hasChanged) {
        this.$router.replace({ query }).catch(() => {});
      }
    },

    // Restore filter state from URL query params
    restoreFiltersFromUrl() {
      const query = this.$route.query;
      if (query.status) this.statusFilter = query.status;
      if (query.device) this.deviceFilter = query.device;
      if (query.serial) this.serialSearch = query.serial;
    },
  },
};
</script>
